"use client";

import { Box, Text } from "@chakra-ui/react";
import { Highlight } from "react-instantsearch";
import {
  type MeiliDocumentSearchHit,
  MEILI_SEARCH_CONFIG,
} from "@/features/search";
import type { MeiliOrigin } from "@/generated/fastapi";
import { CONTENT_ITEM_HIGHLIGHT_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import { CONTENT_ITEM_TEXT_PROPS } from "@/shared/ui/display/ContentItemPrimitives";

const SEARCH_PREVIEW_LINE_CLAMP = { base: 5, md: 4 } as const;
const NON_SEARCH_PREVIEW_LINE_CLAMP = { base: 6, md: 5 } as const;
const COMPACT_SEARCH_PREVIEW_LINE_CLAMP = { base: 3, md: 2 } as const;

type OriginItemPreviewProps = {
  config: (typeof MEILI_SEARCH_CONFIG)[keyof typeof MEILI_SEARCH_CONFIG];
  hit: MeiliDocumentSearchHit;
  isInSearchContext: boolean;
  origin: MeiliOrigin;
  variant?: "default" | "compact";
};

export default function OriginItemPreview({
  config,
  hit,
  isInSearchContext,
  origin,
  variant = "default",
}: OriginItemPreviewProps) {
  const searchLineClamp =
    variant === "compact"
      ? COMPACT_SEARCH_PREVIEW_LINE_CLAMP
      : SEARCH_PREVIEW_LINE_CLAMP;

  if (!isInSearchContext) {
    return (
      <Text
        color="moveon.surface.fg"
        lineClamp={NON_SEARCH_PREVIEW_LINE_CLAMP}
        {...CONTENT_ITEM_TEXT_PROPS}
      >
        {origin.raw_text_input}
      </Text>
    );
  }

  return (
    <Box
      color="moveon.surface.fg"
      css={{
        ...CONTENT_ITEM_HIGHLIGHT_STYLES,
        "& mark": {
          ...CONTENT_ITEM_HIGHLIGHT_STYLES["& mark"],
          background: "var(--chakra-colors-yellow-200)",
        },
      }}
    >
      <Text lineClamp={searchLineClamp} {...CONTENT_ITEM_TEXT_PROPS}>
        <Highlight
          attribute={config.mainContentField}
          hit={hit}
          highlightedTagName="mark"
        />
      </Text>
    </Box>
  );
}
